/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LiteracyScreeningContactModel } from 'src/types/LiteracyScreeningContactModel';
import { LiteracyScreeningDiagnosticMeasuresModel } from 'src/types/LiteracyScreeningDiagnosticMeasuresModel';
import { LiteracyScreeningFileListItemModel } from 'src/types/LiteracyScreeningFileListItemModel';
import { LiteracyScreeningLEASchoolModel } from 'src/types/LiteracyScreeningLEASchoolModel';
import { LiteracyScreeningSchoolModel } from 'src/types/LiteracyScreeningSchoolModel';
import { OptionSetsPageModel } from 'src/types/OptionSetsPageModel';
import { SelectOption } from 'src/types/SelectOption';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  createLiteracyScreening,
  createLiteracyScreeningContact,
  createLiteracyScreeningDiagnostics,
  deleteLiteracyScreeningFileThunk,
  fetchLiteracyScreeningContact,
  fetchLiteracyScreeningDiagnostics,
  fetchLiteracyScreeningFilesByLeaThunk,
  fetchLiteracyScreeningLEASchools,
  fetchLiteracyScreeningOptionSets,
  fetchLiteracyScreeningSchoolYears,
  fetchLiteracyScreeningUpdate,
  updateLiteracyScreening,
  updateLiteracyScreeningContact,
  updateLiteracyScreeningDiagnostics,
  updateLiteracyScreeningDocumentThunk,
  uploadLiteracyScreeningFileThunk,
} from '../thunks/literacyScreeningThunks';

interface LiteracyScreeningState {
  loadingContact: boolean;
  contact: LiteracyScreeningContactModel | null;
  loadingSchoolYears: boolean;
  schoolYears: SelectOption[];
  loadingLEASchools: boolean;
  leaSchools: LiteracyScreeningLEASchoolModel;
  pendingSchool: LiteracyScreeningSchoolModel | null;
  loadingOptionSets: boolean;
  optionSetsPage: OptionSetsPageModel;
  loadingDiagnostics: boolean;
  diagnosticsList: LiteracyScreeningDiagnosticMeasuresModel[];
  loadingLiteracyScreeningUpdate: boolean;
  literacyScreeningUpdate: boolean;
  files: LiteracyScreeningFileListItemModel[];
  loadingFiles: boolean;
  uploadingFile: boolean;
  deletingFile: boolean;
  error: string;
}

export const initialContactFormValues: LiteracyScreeningContactModel = {
  id: '',
  leaIdentifier: '',
  firstName: '',
  lastOrSurname: '',
  position: '',
  email: '',
  telephone: '',
};

export const initialLEASchoolModel: LiteracyScreeningLEASchoolModel = {
  id: '',
  schoolYear: 0,
  leaIdentifier: '',
  districtName: '',
  schoolList: [],
};

export const initialOptionSetsPage: OptionSetsPageModel = {
  module: '',
  page: '',
  optionSets: {},
};

export const initialState: LiteracyScreeningState = {
  loadingContact: false,
  contact: initialContactFormValues,
  loadingSchoolYears: false,
  schoolYears: [],
  loadingLEASchools: false,
  leaSchools: initialLEASchoolModel,
  pendingSchool: null,
  loadingOptionSets: false,
  optionSetsPage: initialOptionSetsPage,
  loadingDiagnostics: false,
  diagnosticsList: [],
  loadingLiteracyScreeningUpdate: false,
  literacyScreeningUpdate: false,
  files: [],
  loadingFiles: false,
  uploadingFile: false,
  deletingFile: false,
  error: '',
};

export const literacyScreeningSlice = createSlice({
  name: 'literacyScreening',
  initialState,
  reducers: {
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = '';
    },
    setLoadingContactList: (state, action: PayloadAction<boolean>) => {
      state.loadingContact = action.payload;
    },
    setContact: (
      state,
      action: PayloadAction<LiteracyScreeningContactModel | null>
    ) => {
      state.contact = action.payload;
    },
    setLiteracyScreeningPendingSchool: (
      state,
      action: PayloadAction<LiteracyScreeningSchoolModel>
    ) => {
      state.pendingSchool = action.payload;
    },
    clearLiteracyScreeningPendingSchool: (state) => {
      state.pendingSchool = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLiteracyScreeningContact.pending, (state, action) => {
      const showLoading = action.meta.arg?.showLoading !== false;
      if (showLoading) {
        state.loadingContact = true;
      }
      state.error = '';
    });
    builder.addCase(
      fetchLiteracyScreeningContact.fulfilled,
      (state, action) => {
        if ('data' in action.payload) {
          state.contact = action.payload.data;
        } else {
          state.contact = { ...initialContactFormValues };
        }
        state.loadingContact = false;
      }
    );
    builder.addCase(fetchLiteracyScreeningContact.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingContact = false;
      }
      state.error =
        action.error.message || 'Failed to fetch literacy screening contact';
    });

    builder.addCase(fetchLiteracyScreeningSchoolYears.pending, (state) => {
      state.schoolYears = [];
      state.loadingSchoolYears = true;
      state.error = '';
    });
    builder.addCase(
      fetchLiteracyScreeningSchoolYears.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.schoolYears = action.payload;
        }
        state.loadingSchoolYears = false;
      }
    );
    builder.addCase(
      fetchLiteracyScreeningSchoolYears.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingSchoolYears = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch literacy screening school years';
      }
    );

    builder.addCase(fetchLiteracyScreeningLEASchools.pending, (state) => {
      state.leaSchools = initialLEASchoolModel;
      state.loadingLEASchools = true;
      state.error = '';
    });
    builder.addCase(
      fetchLiteracyScreeningLEASchools.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.leaSchools = action.payload;
        }
        state.loadingLEASchools = false;
      }
    );
    builder.addCase(
      fetchLiteracyScreeningLEASchools.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingLEASchools = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch literacy screening LEA schools';
      }
    );

    builder.addCase(fetchLiteracyScreeningUpdate.pending, (state) => {
      state.literacyScreeningUpdate = false;
      state.loadingLiteracyScreeningUpdate = true;
      state.error = '';
    });
    builder.addCase(
      fetchLiteracyScreeningUpdate.fulfilled,
      (state, action) => {
        state.literacyScreeningUpdate = Boolean(action.payload);
        state.loadingLiteracyScreeningUpdate = false;
      }
    );
    builder.addCase(
      fetchLiteracyScreeningUpdate.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingLiteracyScreeningUpdate = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch literacy screening update permission';
      }
    );

    builder.addCase(fetchLiteracyScreeningOptionSets.pending, (state) => {
      state.optionSetsPage = initialOptionSetsPage;
      state.loadingOptionSets = true;
      state.error = '';
    });
    builder.addCase(
      fetchLiteracyScreeningOptionSets.fulfilled,
      (state, action) => {
        if (action.payload && !isEmptyObject(action.payload)) {
          state.optionSetsPage = {
            ...initialOptionSetsPage,
            ...action.payload,
            optionSets: action.payload.optionSets || {},
          };
        }
        state.loadingOptionSets = false;
      }
    );
    builder.addCase(
      fetchLiteracyScreeningOptionSets.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingOptionSets = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch literacy screening option sets';
      }
    );

    builder.addCase(
      fetchLiteracyScreeningDiagnostics.pending,
      (state, action) => {
        const showLoading = action.meta.arg?.showLoading !== false;
        if (showLoading) {
          state.diagnosticsList = [];
          state.loadingDiagnostics = true;
        }
        state.error = '';
      }
    );
    builder.addCase(
      fetchLiteracyScreeningDiagnostics.fulfilled,
      (state, action) => {
        if (Array.isArray(action.payload)) {
          state.diagnosticsList = action.payload;
        }
        state.loadingDiagnostics = false;
      }
    );
    builder.addCase(
      fetchLiteracyScreeningDiagnostics.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingDiagnostics = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch literacy screening diagnostics';
      }
    );

    builder.addCase(createLiteracyScreeningDiagnostics.pending, (state) => {
      state.error = '';
    });
    builder.addCase(
      createLiteracyScreeningDiagnostics.fulfilled,
      (state, action) => {
        if (
          typeof action.payload === 'string' &&
          action.payload.trim() !== ''
        ) {
          state.diagnosticsList = [{ ...action.meta.arg, id: action.payload }];
        }
      }
    );
    builder.addCase(
      createLiteracyScreeningDiagnostics.rejected,
      (state, action) => {
        state.error =
          action.error.message ||
          'Failed to create literacy screening diagnostics';
      }
    );

    builder.addCase(updateLiteracyScreeningDiagnostics.pending, (state) => {
      state.error = '';
    });
    builder.addCase(
      updateLiteracyScreeningDiagnostics.fulfilled,
      (state, action) => {
        state.diagnosticsList = [action.meta.arg.model];
      }
    );
    builder.addCase(
      updateLiteracyScreeningDiagnostics.rejected,
      (state, action) => {
        state.error =
          action.error.message ||
          'Failed to update literacy screening diagnostics';
      }
    );

    builder.addCase(updateLiteracyScreeningContact.pending, (state) => {
      state.error = '';
    });
    builder.addCase(
      updateLiteracyScreeningContact.rejected,
      (state, action) => {
        state.error =
          action.error.message || 'Failed to update literacy screening contact';
      }
    );

    builder.addCase(createLiteracyScreeningContact.pending, (state) => {
      state.error = '';
    });
    builder.addCase(
      createLiteracyScreeningContact.rejected,
      (state, action) => {
        state.error =
          action.error.message || 'Failed to create literacy screening contact';
      }
    );

    builder.addCase(createLiteracyScreening.pending, (state) => {
      state.error = '';
    });
    builder.addCase(createLiteracyScreening.rejected, (state, action) => {
      state.error =
        action.error.message || 'Failed to create literacy screening';
    });

    builder.addCase(updateLiteracyScreening.pending, (state) => {
      state.error = '';
    });
    builder.addCase(updateLiteracyScreening.rejected, (state, action) => {
      state.error =
        action.error.message || 'Failed to update literacy screening';
    });

    builder.addCase(uploadLiteracyScreeningFileThunk.pending, (state) => {
      state.uploadingFile = true;
    });
    builder.addCase(uploadLiteracyScreeningFileThunk.fulfilled, (state) => {
      state.uploadingFile = false;
    });
    builder.addCase(
      uploadLiteracyScreeningFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.uploadingFile = false;
        }
      }
    );

    builder.addCase(deleteLiteracyScreeningFileThunk.pending, (state) => {
      state.deletingFile = true;
      state.error = '';
    });
    builder.addCase(deleteLiteracyScreeningFileThunk.fulfilled, (state) => {
      state.deletingFile = false;
    });
    builder.addCase(
      deleteLiteracyScreeningFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.deletingFile = false;
        }
        state.error =
          action.error.message || 'Failed to delete literacy screening file';
      }
    );

    builder.addCase(
      fetchLiteracyScreeningFilesByLeaThunk.pending,
      (state, action) => {
        const showLoading = action.meta.arg?.showLoading !== false;
        if (showLoading) {
          state.files = [];
          state.loadingFiles = true;
        }
        state.error = '';
      }
    );
    builder.addCase(
      fetchLiteracyScreeningFilesByLeaThunk.fulfilled,
      (state, action) => {
        state.files = action.payload || [];
        state.loadingFiles = false;
      }
    );
    builder.addCase(
      fetchLiteracyScreeningFilesByLeaThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFiles = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch literacy screening uploaded files';
      }
    );

    builder.addCase(
      updateLiteracyScreeningDocumentThunk.fulfilled,
      (state, action) => {
        const updated = action.meta.arg.model;
        state.files = state.files.map((file) =>
          file.id === updated.id
            ? {
                ...file,
                mdeStatus: updated.mdeStatus,
                comments: updated.comments,
              }
            : file
        );
      }
    );
    builder.addCase(
      updateLiteracyScreeningDocumentThunk.rejected,
      (state, action) => {
        state.error =
          action.error.message ||
          'Failed to update literacy screening document';
      }
    );
  },
});

export const {
  setError,
  clearError,
  setLoadingContactList,
  setContact,
  setLiteracyScreeningPendingSchool,
  clearLiteracyScreeningPendingSchool,
} = literacyScreeningSlice.actions;

export const selectLiteracyScreeningLoadingContact = (
  state: RootState
): boolean => state.literacyScreening.loadingContact;
export const selectLiteracyScreeningContact = (
  state: RootState
): LiteracyScreeningContactModel | null => state.literacyScreening.contact;
export const selectLiteracyScreeningLoadingSchoolYears = (
  state: RootState
): boolean => state.literacyScreening.loadingSchoolYears;
export const selectLiteracyScreeningSchoolYears = (
  state: RootState
): SelectOption[] => state.literacyScreening.schoolYears;
export const selectLiteracyScreeningCurrentSchoolYear = (
  state: RootState
): string | null => {
  const schoolYear = state.literacyScreening.schoolYears;

  if (!schoolYear || schoolYear.length === 0) {
    return null;
  }

  return schoolYear[0].value;
};
export const selectLiteracyScreeningLoadingLEASchools = (
  state: RootState
): boolean => state.literacyScreening.loadingLEASchools;
export const selectLiteracyScreeningLEASchools = (
  state: RootState
): LiteracyScreeningLEASchoolModel => state.literacyScreening.leaSchools;
export const selectLiteracyScreeningSchoolList = (
  state: RootState
): LiteracyScreeningSchoolModel[] =>
  state.literacyScreening.leaSchools.schoolList || [];
export const selectLiteracyScreeningLoadingUpdate = (
  state: RootState
): boolean => state.literacyScreening.loadingLiteracyScreeningUpdate;
export const selectLiteracyScreeningUpdate = (
  state: RootState
): boolean => state.literacyScreening.literacyScreeningUpdate;
export const selectLiteracyScreeningPendingSchool = (
  state: RootState
): LiteracyScreeningSchoolModel | null => state.literacyScreening.pendingSchool;
export const selectLiteracyScreeningLoadingOptionSets = (
  state: RootState
): boolean => state.literacyScreening.loadingOptionSets;
export const selectLiteracyScreeningOptionSetsPage = (
  state: RootState
): OptionSetsPageModel => state.literacyScreening.optionSetsPage;
export const selectLiteracyScreeningOptionSets = (
  state: RootState
): Record<string, SelectOption[]> =>
  state.literacyScreening.optionSetsPage.optionSets || {};
export const selectLiteracyScreeningLoadingDiagnostics = (
  state: RootState
): boolean => state.literacyScreening.loadingDiagnostics;
export const selectLiteracyScreeningDiagnosticsList = (
  state: RootState
): LiteracyScreeningDiagnosticMeasuresModel[] =>
  state.literacyScreening.diagnosticsList;
export const selectLiteracyScreeningDiagnostics = (
  state: RootState
): LiteracyScreeningDiagnosticMeasuresModel | null =>
  state.literacyScreening.diagnosticsList[0] || null;
export const selectLiteracyScreeningFiles = (
  state: RootState
): LiteracyScreeningFileListItemModel[] => state.literacyScreening.files;
export const selectLiteracyScreeningLoadingFiles = (
  state: RootState
): boolean => state.literacyScreening.loadingFiles;

export default literacyScreeningSlice.reducer;
